import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCruiseMetadata } from '../context/CruiseMetadataContext';
import { applyNormalizedClassUpdates, NormalizedClassUpdate } from '../services/shipClassService';
import { 
  Loader2, 
  ChevronLeft, 
  SlidersHorizontal, 
  Search, 
  CheckCircle, 
  AlertTriangle, 
  Layers, 
  Sparkles,
  Edit2,
  Trash2,
  Save,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';

type ShipEntry = {
  cruiseLineName: string;
  shipName: string;
  originalClass: string;
  normalizedClass: string;
};

type ClassVariant = {
  className: string;
  ships: {
    cruiseLineName: string;
    shipName: string;
    normalizedClass?: string;
  }[];
  existingNormalizedNames: string[];
};

type ClassGroupView = {
  id: string;
  variants: ClassVariant[];
  selectedNormalized: string;
  apply: boolean;
  shipCount: number;
};

const normalizeClassString = (value: string) =>
  value
    .toLowerCase()
    .replace(/class/g, '')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const calculateClassSimilarity = (a: string, b: string) => {
  const aNorm = normalizeClassString(a);
  const bNorm = normalizeClassString(b);
  if (!aNorm || !bNorm) return 0;
  if (aNorm === bNorm) return 1;
  if (aNorm.includes(bNorm) || bNorm.includes(aNorm)) {
    const longer = Math.max(aNorm.length, bNorm.length);
    const shorter = Math.min(aNorm.length, bNorm.length);
    return shorter / longer;
  }
  const aTokens = Array.from(new Set(aNorm.split(' ')));
  const bTokens = Array.from(new Set(bNorm.split(' ')));
  const intersection = aTokens.filter((t) => bTokens.includes(t));
  const union = new Set([...aTokens, ...bTokens]).size;
  return union === 0 ? 0 : intersection.length / union;
};

const ShipClassNormalization = () => {
  const { cruiseLines, loading, refreshCruiseMetadata } = useCruiseMetadata();

  const [activeTab, setActiveTab] = useState<'candidates' | 'directory'>('candidates');

  // Tab 1: Normalization Candidates States
  const [similarityThreshold, setSimilarityThreshold] = useState(0.65);
  const [searchTerm, setSearchTerm] = useState('');
  const [groups, setGroups] = useState<ClassGroupView[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Tab 2: Directory States
  const [directorySearch, setDirectorySearch] = useState('');
  const [editingShipKey, setEditingShipKey] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [isInlineSaving, setIsInlineSaving] = useState(false);

  // Directory Data Memo
  const allShipsList = useMemo<ShipEntry[]>(() => {
    const list: ShipEntry[] = [];
    cruiseLines.forEach((line) => {
      Object.entries(line.shipMetadata || {}).forEach(([shipName, metadata]) => {
        list.push({
          cruiseLineName: line.name,
          shipName,
          originalClass: metadata.shipClass || 'None',
          normalizedClass: metadata.normalizedClass || '',
        });
      });
    });
    return list.sort((a, b) => a.shipName.localeCompare(b.shipName));
  }, [cruiseLines]);

  const filteredDirectory = useMemo(() => {
    if (!directorySearch.trim()) return allShipsList;
    const term = directorySearch.toLowerCase();
    return allShipsList.filter(
      (ship) =>
        ship.shipName.toLowerCase().includes(term) ||
        ship.cruiseLineName.toLowerCase().includes(term) ||
        ship.originalClass.toLowerCase().includes(term) ||
        ship.normalizedClass.toLowerCase().includes(term)
    );
  }, [allShipsList, directorySearch]);

  const existingNormalizedNames = useMemo(() => {
    const names = new Set<string>();
    cruiseLines.forEach((line) => {
      Object.values(line.shipMetadata || {}).forEach((metadata) => {
        if (metadata.normalizedClass) {
          names.add(metadata.normalizedClass);
        }
      });
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [cruiseLines]);

  const classVariants = useMemo<ClassVariant[]>(() => {
    const variantMap = new Map<string, ClassVariant>();

    cruiseLines.forEach((line) => {
      Object.entries(line.shipMetadata || {}).forEach(([shipName, metadata]) => {
        if (!metadata.shipClass) return;
        const key = metadata.shipClass;
        if (!variantMap.has(key)) {
          variantMap.set(key, {
            className: metadata.shipClass,
            ships: [],
            existingNormalizedNames: [],
          });
        }
        const variant = variantMap.get(key)!;
        variant.ships.push({
          cruiseLineName: line.name,
          shipName,
          normalizedClass: metadata.normalizedClass,
        });
        if (metadata.normalizedClass) {
          variant.existingNormalizedNames.push(metadata.normalizedClass);
        }
      });
    });

    return Array.from(variantMap.values()).sort((a, b) => b.ships.length - a.ships.length);
  }, [cruiseLines]);

  const baseGroups = useMemo(() => {
    const processed = new Set<string>();
    const groups: ClassVariant[][] = [];

    classVariants.forEach((variant) => {
      if (processed.has(variant.className)) return;
      const group: ClassVariant[] = [variant];
      processed.add(variant.className);

      classVariants.forEach((other) => {
        if (processed.has(other.className) || other.className === variant.className) return;
        const similarity = calculateClassSimilarity(variant.className, other.className);
        if (similarity >= similarityThreshold) {
          group.push(other);
          processed.add(other.className);
        }
      });

      groups.push(group);
    });

    return groups;
  }, [classVariants, similarityThreshold]);

  useEffect(() => {
    const nextGroups: ClassGroupView[] = [];

    baseGroups.forEach((group, idx) => {
      const hasVariation = group.length > 1;
      // Skip groups with no variation (single class)
      if (!hasVariation) {
        return;
      }

      const allShips = group.flatMap((variant) => variant.ships);
      const allNormalized = allShips.every((s) => !!s.normalizedClass);
      const normalizedValues = Array.from(new Set(allShips.map((s) => s.normalizedClass).filter(Boolean)));

      if (allNormalized && normalizedValues.length === 1) {
        return;
      }

      const normalizedCounts: Record<string, number> = {};
      let totalShips = 0;

      group.forEach((variant) => {
        totalShips += variant.ships.length;
        variant.existingNormalizedNames.forEach((name) => {
          normalizedCounts[name] = (normalizedCounts[name] || 0) + 1;
        });
      });

      const mostUsedNormalized =
        Object.entries(normalizedCounts).sort((a, b) => b[1] - a[1])[0]?.[0];

      const heaviestVariant = group.slice().sort((a, b) => b.ships.length - a.ships.length)[0];
      const defaultName = mostUsedNormalized || heaviestVariant?.className || '';

      nextGroups.push({
        id: `group-${idx}`,
        variants: group,
        selectedNormalized: defaultName,
        apply: true,
        shipCount: totalShips,
      });
    });

    setGroups(nextGroups);
  }, [baseGroups]);

  const filteredGroups = useMemo(() => {
    if (!searchTerm.trim()) return groups;
    const term = searchTerm.toLowerCase();
    return groups.filter((group) =>
      group.variants.some(
        (variant) =>
          variant.className.toLowerCase().includes(term) ||
          group.selectedNormalized.toLowerCase().includes(term),
      ),
    );
  }, [groups, searchTerm]);

  const handleNormalizedChange = (groupId: string, value: string) => {
    setGroups((prev) =>
      prev.map((group) =>
        group.id === groupId ? { ...group, selectedNormalized: value } : group,
      ),
    );
  };

  const toggleGroupApply = (groupId: string) => {
    setGroups((prev) =>
      prev.map((group) => (group.id === groupId ? { ...group, apply: !group.apply } : group)),
    );
  };

  const handleApply = async () => {
    const selected = groups.filter((g) => g.apply);
    if (selected.length === 0) {
      toast.error('Select at least one group to update.');
      return;
    }

    const updatesMap = new Map<string, NormalizedClassUpdate>();
    for (const group of selected) {
      const normalizedValue = group.selectedNormalized.trim();
      if (!normalizedValue) {
        toast.error('Normalized class name cannot be empty.');
        return;
      }
      group.variants.forEach((variant) => {
        variant.ships.forEach((ship) => {
          const key = `${ship.cruiseLineName}|${ship.shipName}`;
          updatesMap.set(key, {
            cruiseLine: ship.cruiseLineName,
            shipName: ship.shipName,
            normalizedClass: normalizedValue,
          });
        });
      });
    }

    const updates = Array.from(updatesMap.values());
    setIsSaving(true);
    try {
      const result = await applyNormalizedClassUpdates(updates);
      toast.success(
        `Updated ${result.updatedShips} ship${result.updatedShips === 1 ? '' : 's'} across ${result.updatedCruiseLines} cruise line${result.updatedCruiseLines === 1 ? '' : 's'}.`,
      );
      if (result.missingCruiseLines.length > 0) {
        toast.error(`Missing cruise lines: ${result.missingCruiseLines.join(', ')}`);
      }
      await refreshCruiseMetadata();
    } catch (error) {
      console.error('Failed to update normalized classes', error);
      toast.error('Failed to update normalized classes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Tab 2 Actions
  const handleStartEdit = (ship: ShipEntry) => {
    setEditingShipKey(`${ship.cruiseLineName}|${ship.shipName}`);
    setEditingValue(ship.normalizedClass);
  };

  const handleCancelEdit = () => {
    setEditingShipKey(null);
    setEditingValue('');
  };

  const handleSaveInline = async (ship: ShipEntry) => {
    setIsInlineSaving(true);
    try {
      await applyNormalizedClassUpdates([
        {
          cruiseLine: ship.cruiseLineName,
          shipName: ship.shipName,
          normalizedClass: editingValue.trim(),
        },
      ]);
      toast.success(`Updated ${ship.shipName} class name.`);
      setEditingShipKey(null);
      await refreshCruiseMetadata();
    } catch (error) {
      console.error('Inline update failed:', error);
      toast.error('Failed to update normalized name.');
    } finally {
      setIsInlineSaving(false);
    }
  };

  const handleClearInline = async (ship: ShipEntry) => {
    if (!window.confirm(`Are you sure you want to remove class normalization for ${ship.shipName}?`)) {
      return;
    }
    setIsInlineSaving(true);
    try {
      await applyNormalizedClassUpdates([
        {
          cruiseLine: ship.cruiseLineName,
          shipName: ship.shipName,
          normalizedClass: '', // will trigger delete in service
        },
      ]);
      toast.success(`Removed class normalization for ${ship.shipName}.`);
      await refreshCruiseMetadata();
    } catch (error) {
      console.error('Failed to clear class normalization:', error);
      toast.error('Failed to clear normalization.');
    } finally {
      setIsInlineSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <motion.div
      className="max-w-6xl mx-auto space-y-6 pb-20"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/admin"
          className="mr-4 p-2 rounded-full hover:bg-gray-200 transition-colors"
          aria-label="Go to Admin"
        >
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            <Layers className="h-8 w-8 text-teal-600" />
            Ship Class Normalization
          </h1>
          <p className="text-gray-600 mt-1">
            Fuzzy-match ship classes, assign normalized names, and manage existing normalizations.
          </p>
        </div>
      </div>

      {/* Tabs selector */}
      <div className="flex border-b border-gray-200 text-sm font-semibold">
        <button
          onClick={() => setActiveTab('candidates')}
          className={`pb-3 px-4 border-b-2 transition-colors ${
            activeTab === 'candidates'
              ? 'border-teal-600 text-teal-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Normalization Candidates ({filteredGroups.length})
        </button>
        <button
          onClick={() => setActiveTab('directory')}
          className={`pb-3 px-4 border-b-2 transition-colors ${
            activeTab === 'directory'
              ? 'border-teal-600 text-teal-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          All Ships Directory ({allShipsList.length})
        </button>
      </div>

      {activeTab === 'candidates' ? (
        <>
          {/* Toolbar */}
          <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm space-y-4">
            <div className="flex flex-col gap-4 md:flex-row md:items-end">
              <div className="flex-1">
                <label htmlFor="class-search-input" className="block text-xs font-semibold text-gray-700 mb-1.5">Search Classes</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    id="class-search-input"
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by class name or normalized name..."
                    className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                    aria-label="Search by class name or normalized name"
                  />
                </div>
              </div>
              <div className="w-full md:w-72">
                <label htmlFor="similarity-threshold-slider" className="block text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
                  <SlidersHorizontal className="h-3.5 w-3.5 text-gray-500" />
                  Similarity Threshold: {(similarityThreshold * 100).toFixed(0)}%
                </label>
                <input
                  id="similarity-threshold-slider"
                  type="range"
                  min="0.4"
                  max="0.95"
                  step="0.05"
                  value={similarityThreshold}
                  onChange={(e) => setSimilarityThreshold(parseFloat(e.target.value))}
                  className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-600 focus:outline-none focus:ring-2 focus:ring-accent"
                  aria-label="Similarity threshold"
                />
                <div
                  className={`mt-2 rounded p-1.5 border text-[11px] ${
                    similarityThreshold <= 0.5 ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-gray-50 border-gray-200 text-gray-600'
                  }`}
                >
                  {similarityThreshold <= 0.5 ? (
                    <div className="flex items-start gap-1">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>Lower thresholds may create loose matches. Review groups carefully.</span>
                    </div>
                  ) : (
                    <span>Lower = more matches (catch variants). Higher = stricter matches.</span>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs pt-2">
              <div className="rounded-lg border border-gray-100 p-3 bg-gray-50/50 flex items-center gap-2">
                <Layers className="h-5 w-5 text-teal-600" />
                <div>
                  <p className="text-gray-900 font-bold">{classVariants.length}</p>
                  <p className="text-gray-500">Class variants detected</p>
                </div>
              </div>
              <div className="rounded-lg border border-gray-100 p-3 bg-gray-50/50 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-indigo-600" />
                <div>
                  <p className="text-gray-900 font-bold">{existingNormalizedNames.length}</p>
                  <p className="text-gray-500">Existing normalized names</p>
                </div>
              </div>
              <div className="rounded-lg border border-gray-100 p-3 bg-gray-50/50 flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
                <div>
                  <p className="text-gray-900 font-bold">
                    {groups.filter((g) => g.apply).reduce((sum, g) => sum + g.shipCount, 0)}
                  </p>
                  <p className="text-gray-500">Ships selected for update</p>
                </div>
              </div>
            </div>
          </div>

          {filteredGroups.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center shadow-sm">
              <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-800 mb-1">No matching groups</h3>
              <p className="text-gray-500 text-sm">
                Adjust your search or threshold to find class variations to normalize.
              </p>
            </div>
          ) : (
            <div className="space-y-4 mb-6">
              {filteredGroups.map((group) => (
                <motion.div
                  key={group.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm"
                >
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between border-b border-gray-100 pb-4">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-bold text-gray-900">
                          {group.variants.length > 1
                            ? `${group.variants.length} class variations`
                            : group.variants[0].className}
                        </h3>
                        <span className="text-xs font-semibold bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                          {group.shipCount} ship{group.shipCount === 1 ? '' : 's'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Select a normalized name to assign to the variants in this group.
                      </p>
                    </div>

                    <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 cursor-pointer self-start">
                      <span>Apply</span>
                      <input
                        type="checkbox"
                        checked={group.apply}
                        onChange={() => toggleGroupApply(group.id)}
                        className="h-4 w-4 text-teal-600 focus:ring-teal-500 rounded border-gray-300"
                      />
                    </label>
                  </div>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-5">
                    <div className="md:col-span-1">
                      <label htmlFor={`normalized-class-input-${group.id}`} className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">
                        Normalized Class Name
                      </label>
                      <input
                        id={`normalized-class-input-${group.id}`}
                        list="normalized-class-names"
                        value={group.selectedNormalized}
                        onChange={(e) => handleNormalizedChange(group.id, e.target.value)}
                        placeholder="Enter normalized class name"
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                        aria-label="Normalized Class Name"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <span className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">Variants</span>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {group.variants.map((variant) => (
                          <div
                            key={variant.className}
                            className="border border-gray-200 rounded-md p-3 bg-gray-50/50 text-xs"
                          >
                            <p className="font-bold text-gray-900 leading-tight">{variant.className}</p>
                            <p className="text-[10px] text-gray-500 mt-0.5 mb-2">
                              {variant.ships.length} ship{variant.ships.length === 1 ? '' : 's'}
                            </p>
                            <div className="space-y-1 text-gray-600">
                              {variant.ships.slice(0, 3).map((ship) => (
                                <div key={`${ship.cruiseLineName}-${ship.shipName}`} className="truncate">
                                  {ship.cruiseLineName} • {ship.shipName}
                                  {ship.normalizedClass && (
                                    <span className="ml-1 font-semibold text-teal-600">
                                      ({ship.normalizedClass})
                                    </span>
                                  )}
                                </div>
                              ))}
                              {variant.ships.length > 3 && (
                                <div className="text-gray-400 italic">
                                  +{variant.ships.length - 3} more
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          <datalist id="normalized-class-names">
            {existingNormalizedNames.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>

          {/* Sticky Bottom Actions */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 sticky bottom-4 shadow-md flex items-center justify-between gap-4">
            <div className="text-xs text-gray-600 max-w-lg">
              Applying changes will write the normalized class name to all ships within the selected groups in Firestore.
            </div>
            <button
              onClick={handleApply}
              disabled={isSaving || groups.filter((g) => g.apply).length === 0}
              className="flex items-center gap-1.5 px-5 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md text-sm font-semibold shadow disabled:opacity-50 transition-colors"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Update Selected ({groups.filter((g) => g.apply).length})
                </>
              )}
            </button>
          </div>
        </>
      ) : (
        /* Tab 2: Directory View */
        <div className="space-y-4">
          {/* Search bar specifically for Tab 2 */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm flex gap-3 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                id="directory-search-input"
                type="text"
                value={directorySearch}
                onChange={(e) => setDirectorySearch(e.target.value)}
                placeholder="Search ships, cruise lines, classes, or normalizations..."
                className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                aria-label="Search ships, cruise lines, classes, or normalizations"
              />
            </div>
            {directorySearch && (
              <button 
                onClick={() => setDirectorySearch('')}
                className="text-xs font-semibold text-gray-500 hover:text-gray-800"
              >
                Clear
              </button>
            )}
          </div>

          {/* Ships Table List */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm text-left">
                <thead className="bg-gray-50 font-semibold text-xs text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="px-6 py-3">Cruise Line</th>
                    <th className="px-6 py-3">Ship Name</th>
                    <th className="px-6 py-3">Original Class</th>
                    <th className="px-6 py-3">Normalized Class</th>
                    <th className="px-6 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 text-gray-700">
                  {filteredDirectory.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-10 text-center text-gray-500">
                        No ships found matching your filters.
                      </td>
                    </tr>
                  ) : (
                    filteredDirectory.map((ship) => {
                      const isEditing = editingShipKey === `${ship.cruiseLineName}|${ship.shipName}`;

                      return (
                        <tr key={`${ship.cruiseLineName}-${ship.shipName}`} className="hover:bg-gray-50/50">
                          <td className="px-6 py-3.5 font-medium text-gray-900">{ship.cruiseLineName}</td>
                          <td className="px-6 py-3.5">{ship.shipName}</td>
                          <td className="px-6 py-3.5">
                            <span className="bg-gray-100 border border-gray-200 text-gray-700 px-2 py-0.5 rounded text-xs font-mono">
                              {ship.originalClass}
                            </span>
                          </td>
                          <td className="px-6 py-3.5">
                            {isEditing ? (
                              <input
                                list="normalized-class-names"
                                type="text"
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:border-teal-500 font-semibold"
                                placeholder="Normalized name"
                                aria-label="Normalized name"
                              />
                            ) : ship.normalizedClass ? (
                              <span className="inline-flex items-center gap-1 bg-teal-50 border border-teal-100 text-teal-800 px-2.5 py-0.5 rounded text-xs font-bold">
                                <Sparkles className="h-3 w-3 text-teal-600" />
                                {ship.normalizedClass}
                              </span>
                            ) : (
                              <span className="text-gray-400 italic text-xs">Unnormalized</span>
                            )}
                          </td>
                          <td className="px-6 py-3.5 text-right whitespace-nowrap">
                            {isEditing ? (
                              <div className="flex justify-end gap-1.5">
                                <button
                                  onClick={() => handleSaveInline(ship)}
                                  disabled={isInlineSaving}
                                  className="p-1 rounded text-emerald-600 hover:bg-emerald-50 transition-colors"
                                  title="Save"
                                >
                                  <Save className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  disabled={isInlineSaving}
                                  className="p-1 rounded text-red-600 hover:bg-red-50 transition-colors"
                                  title="Cancel"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex justify-end gap-1.5">
                                <button
                                  onClick={() => handleStartEdit(ship)}
                                  disabled={isInlineSaving}
                                  className="p-1.5 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
                                  title="Edit normalization"
                                >
                                  <Edit2 className="h-4 w-4" />
                                </button>
                                {ship.normalizedClass && (
                                  <button
                                    onClick={() => handleClearInline(ship)}
                                    disabled={isInlineSaving}
                                    className="p-1.5 rounded text-red-500 hover:bg-red-50 transition-colors"
                                    title="Remove normalization"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default ShipClassNormalization;
